import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Stack
} from '@mui/material';
import { timeUtils } from '../utils/timeManager';
import {
  DragIndicator as DragIcon,
  AccessTime as TimeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Code as MarkdownIcon,
  Sort as SortIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { marked } from 'marked';

// 配置 marked 安全選項
marked.setOptions({
  breaks: true,
  gfm: true
});

interface NoteRecord {
  keyNumber: number;
  timecode: string;
  note: string;
  timestamp: number;
  isActive: boolean;
  hasNote: boolean;
  originalKeyNumber?: number;
}

interface NoteRecordsDialogProps {
  open: boolean;
  onClose: () => void;
  numberKeys: Record<string, any>;
  onReorder: (newOrder: number[]) => void;
  onEditNote: (keyNumber: number, note: string) => void;
  onDeleteNote: (keyNumber: number) => void;
  onTimecodeClick?: (keyNumber: number, timestamp: number) => void;
  onCleanupData?: () => void;
  onSaveReorderedData?: (reorderedRecords: any[]) => void;
  currentEditingKey?: number | null;
  currentEditingContent?: string;
  onClearTimecode?: (keyNumber: number) => void;
  // 版本管理相關
  onVersionManagement?: () => void;
  isUserLoggedIn?: boolean;
  videoUrl?: string;
  user?: any;
}

// 可排序的記錄組件
interface SortableRecordProps {
  record: NoteRecord;
  index: number;
  keyNumber: number;
  isMobile: boolean;
  theme: any;
  onStartEdit: (keyNumber: number, note: string) => void;
  onDelete: (keyNumber: number) => void;
  onTimecodeClick?: (keyNumber: number, timestamp: number) => void;
}

const SortableRecord: React.FC<SortableRecordProps> = ({
  record,
  index,
  keyNumber,
  isMobile,
  theme,
  onStartEdit,
  onDelete,
  onTimecodeClick
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: `record-${index}-${record.keyNumber}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 8 : 2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        p: isMobile ? 0.5 : 1,
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        transform: isDragging ? 'rotate(2deg)' : 'none',
        transition: 'all 0.2s ease',
        border: `2px solid ${isDragging ? theme.palette.primary.main : 'transparent'}`,
        width: '100%',
        gap: 0.5
      }}
    >
      {/* 第一行：拖拽手柄 + 時間碼 + 操作按鈕 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        width: '100%',
        minHeight: '28px'
      }}>
        {/* 拖拽手柄 */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            p: 0.5,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <DragIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
        </Box>

        {/* 時間碼 - 根據數字鍵狀態顯示不同顏色 */}
        <Typography
          variant="body1"
          sx={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: record.isActive ? '#000000' : theme.palette.text.secondary,
            cursor: 'pointer',
            flex: 1,
            textAlign: 'left',
            pl: 1,
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
          onClick={() => {
            // 只觸發pickup動作，不重複執行播放邏輯
            if (onTimecodeClick) {
              onTimecodeClick(record.keyNumber, record.timestamp);
            }
            // 自動關閉視窗
            if (record.isActive && record.timestamp > 0) {
              // 延遲關閉視窗，讓pickup動作完成
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('closeNoteRecordsDialog'));
              }, 200);
            }
          }}
        >
          {record.timecode}
        </Typography>

        {/* 操作按鈕 */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onStartEdit(record.keyNumber, record.note)}
            sx={{ p: 0.5 }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(record.keyNumber)}
            sx={{ p: 0.5, color: theme.palette.error.main }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* 第二行：Note 內容 - 縮版並支持點擊編輯，支援 Markdown */}
      <Box 
        sx={{ 
          minHeight: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          px: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: 1
          }
        }}
        onClick={() => onStartEdit(record.keyNumber, record.note)}
      >
        {record.note ? (
          <Box
            dangerouslySetInnerHTML={{
              __html: marked(record.note, { breaks: true })
            }}
            sx={{
              width: '100%',
              fontSize: '14px',
              lineHeight: 1.3,
              color: '#d32f2f',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                margin: '4px 0 2px 0',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#d32f2f'
              },
              '& p': {
                margin: '2px 0',
                lineHeight: 1.3,
                color: '#d32f2f'
              },
              '& code': {
                backgroundColor: '#f0f0f0',
                padding: '1px 3px',
                borderRadius: '2px',
                fontFamily: 'monospace',
                fontSize: '12px',
                color: '#d32f2f'
              },
              '& pre': {
                backgroundColor: '#f5f5f5',
                padding: '4px',
                borderRadius: '3px',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                fontSize: '12px',
                margin: '2px 0',
                color: '#d32f2f'
              },
              '& blockquote': {
                borderLeft: '2px solid #ddd',
                margin: '4px 0',
                padding: '2px 8px',
                backgroundColor: '#f9f9f9',
                fontSize: '12px',
                color: '#d32f2f'
              },
              '& ul, & ol': {
                paddingLeft: '16px',
                margin: '2px 0'
              },
              '& li': {
                margin: '1px 0',
                fontSize: '12px',
                color: '#d32f2f'
              },
              '& a': {
                color: '#d32f2f',
                textDecoration: 'none',
                fontSize: '12px'
              },
              '& strong': {
                fontWeight: 'bold',
                color: '#d32f2f'
              },
              '& em': {
                fontStyle: 'italic',
                color: '#d32f2f'
              }
            }}
          />
        ) : null}
      </Box>
    </Paper>
  );
};

// 格式化時間碼的輔助函數
const formatTimecode = (timestamp: number): string => {
  return timeUtils.toTimecode(timestamp);
};

const NoteRecordsDialog: React.FC<NoteRecordsDialogProps> = ({
  open,
  onClose,
  numberKeys,
  onReorder,
  onEditNote,
  onDeleteNote,
  onTimecodeClick,
  onCleanupData,
  onSaveReorderedData,
  currentEditingKey,
  currentEditingContent,
  onClearTimecode,
  onVersionManagement,
  isUserLoggedIn,
  videoUrl,
  user
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [noteRecords, setNoteRecords] = useState<NoteRecord[]>([]);
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'edit' | 'preview'>('edit');

  // 配置傳感器
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 監聽關閉事件
  useEffect(() => {
    const handleCloseEvent = () => {
      onClose();
    };

    window.addEventListener('closeNoteRecordsDialog', handleCloseEvent, { passive: true });
    
    return () => {
      window.removeEventListener('closeNoteRecordsDialog', handleCloseEvent);
    };
  }, [onClose]);

  // 從 numberKeys 提取所有數字鍵記錄
  useEffect(() => {
    if (open && numberKeys) {
      console.log('🔍 NoteRecordsDialog 接收到的 numberKeys:', numberKeys);
      
      const records: NoteRecord[] = [];
      
      const allKeyNumbers = Object.keys(numberKeys)
        .map(key => parseInt(key))
        .filter(key => !isNaN(key))
        .sort((a, b) => a - b);
      
      if (allKeyNumbers.length === 0) {
        for (let i = 1; i <= 18; i++) {
          records.push({
            keyNumber: i,
            timecode: '00:00:00 00',
            note: '',
            timestamp: 0,
            isActive: false,
            hasNote: false
          });
        }
      } else {
        // 為所有存在的鍵創建記錄
        allKeyNumbers.forEach(keyNumber => {
          const state = numberKeys[keyNumber.toString()];
          if (state) {
            // 🔧 修復：優先使用 timestamp 生成正確的時間碼，因為 VBID 中的 timecode 可能不正確
            let formattedTimecode = '00:00:00 00';
            
            // 優先使用 timestamp 生成時間碼（因為 VBID 中的 timecode 可能都是 00:00:00 00）
            if (state.timestamp !== null && state.timestamp !== undefined && state.timestamp >= 0 && state.timestamp <= 1000000) {
              formattedTimecode = formatTimecode(state.timestamp);
              console.log(`✅ 使用 timestamp 生成時間碼: ${state.timestamp} → ${formattedTimecode}`);
            } else if (state.timecode && state.timecode.trim() !== '' && state.timecode !== '00:00:00 00') {
              // 只有在 timestamp 無效且 timecode 不是 00:00:00 00 時，才使用 timecode
              const timecodeRegex = /^\d{2}:\d{2}:\d{2} \d{2}$/;
              if (timecodeRegex.test(state.timecode)) {
                formattedTimecode = state.timecode;
                console.log(`✅ 使用有效的 timecode 格式: ${state.timecode}`);
              }
            }
            console.log(`🔍 數字鍵 ${keyNumber} 時間碼格式化:`, {
              原始時間碼: state.timecode,
              時間戳: state.timestamp,
              格式化後: formattedTimecode
            });
            
            records.push({
              keyNumber,
              timecode: formattedTimecode,
              note: state.note || '',
              timestamp: state.timestamp || 0,
              isActive: state.isActive || false,
              hasNote: state.hasNote || false
            });
          }
        });
        
        // 為缺失的鍵（1-18）創建空記錄
        for (let i = 1; i <= 18; i++) {
          if (!records.find(r => r.keyNumber === i)) {
            records.push({
              keyNumber: i,
              timecode: '00:00:00 00',
              note: '',
              timestamp: 0,
              isActive: false,
              hasNote: false
            });
          }
        }
        
        // 按數字鍵排序
        records.sort((a, b) => a.keyNumber - b.keyNumber);
      }
      
      // 強制更新當前正在編輯的數字鍵的 Note 內容
      if (currentEditingKey && currentEditingContent !== undefined) {
        const editingRecord = records.find(r => r.keyNumber === currentEditingKey);
        if (editingRecord) {
          editingRecord.note = currentEditingContent;
          console.log(`🔄 強制更新數字鍵 ${currentEditingKey} 的 Note 內容:`, currentEditingContent);
        }
      }
      
      console.log('📝 Note 記錄載入完成:', {
        總數: records.length,
        實際數字鍵: allKeyNumbers,
        記錄: records.map(r => ({
          keyNumber: r.keyNumber,
          timecode: r.timecode,
          timestamp: r.timestamp,
          isActive: r.isActive,
          hasNote: r.hasNote
        }))
      });
      
      // 調試：檢查特定數字鍵的狀態
      console.log('🔍 調試特定數字鍵狀態:', {
        '7': numberKeys['7'],
        '8': numberKeys['8'],
        '9': numberKeys['9']
      });
      
      setNoteRecords(records);
    }
  }, [open, numberKeys, currentEditingKey, currentEditingContent]);

  // 拖拽處理邏輯
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // 從 ID 中提取索引
      const oldIndex = parseInt(active.id.toString().split('-')[1]);
      const newIndex = over ? parseInt(over.id.toString().split('-')[1]) : -1;

      if (!isNaN(oldIndex) && !isNaN(newIndex) && oldIndex !== newIndex) {
        const newRecords = arrayMove(noteRecords, oldIndex, newIndex);
        
        // 重新分配數字鍵
        const reorderedItems = newRecords.map((item, index) => ({
          ...item,
          keyNumber: index + 1,
          originalKeyNumber: item.keyNumber
        }));

        setNoteRecords(reorderedItems);
        
        const newOrder = reorderedItems.map(item => item.keyNumber);
        onReorder(newOrder);
        
        console.log('🔄 拖拽重新排序完成:', {
          原始順序: noteRecords.map(item => item.keyNumber),
          新順序: newOrder
        });
      }
    }
  };

  // 開始編輯
  const handleStartEdit = (keyNumber: number, note: string) => {
    setEditingKey(keyNumber);
    setEditText(note);
  };

  // 保存編輯
  const handleSaveEdit = () => {
    if (editingKey !== null) {
      const noteText = editText.trim();
      
      try {
        if (onEditNote) {
          onEditNote(editingKey, noteText);
          console.log(`✅ Note ${editingKey} 已保存到 Firestore: "${noteText}"`);
        } else {
          console.error('❌ onEditNote 回調函數未定義');
        }
        
        setEditingKey(null);
        setEditText('');
      } catch (error) {
        console.error('❌ 保存 Note 時發生錯誤:', error);
        alert('保存失敗，請重試');
      }
    }
  };

  // 取消編輯
  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditText('');
  };

  // 刪除 Note
  const handleDelete = (keyNumber: number) => {
    if (window.confirm(`確定要刪除數字鍵 ${keyNumber} 的 Note 嗎？`)) {
      onDeleteNote(keyNumber);
      if (onClearTimecode) {
        onClearTimecode(keyNumber);
      }
    }
  };

  // 開啟說明對話框
  const handleOpenHelp = () => {
    setHelpDialogOpen(true);
  };

  // 關閉說明對話框
  const handleCloseHelp = () => {
    setHelpDialogOpen(false);
  };

  // 清理數據
  const handleCleanup = () => {
    if (onCleanupData) {
      onCleanupData();
    }
  };


  // 按時間碼從小到大排序
  const handleAutoSort = () => {
    console.log('🔄 開始自動排序，按時間碼從小到大');
    
    // 🔧 修復：使用 timecode 解析正確的時間值進行排序
    const parseTimecodeToSeconds = (timecode: string): number => {
      if (!timecode || timecode === '00:00:00 00') return 0;
      
      const match = timecode.match(/^(\d{2}):(\d{2}):(\d{2})\s*[: ]\s*(\d{2})$/);
      if (!match) return 0;
      
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const seconds = parseInt(match[3], 10);
      const frames = parseInt(match[4], 10);
      
      return hours * 3600 + minutes * 60 + seconds + frames / 30;
    };
    
    // 分離有時間碼和沒有時間碼的記錄
    const recordsWithTimecode = noteRecords.filter(record => 
      record.timecode && record.timecode.trim() !== '' && record.timecode !== '00:00:00 00'
    );
    
    const recordsWithoutTimecode = noteRecords.filter(record => 
      !record.timecode || record.timecode === '00:00:00 00' || record.timecode.trim() === ''
    );
    
    console.log('📊 排序前統計:', {
      總記錄數: noteRecords.length,
      有時間碼: recordsWithTimecode.length,
      無時間碼: recordsWithoutTimecode.length
    });
    
    // 只對有時間碼的記錄進行排序，使用正確的時間計算
    const sortedRecordsWithTimecode = recordsWithTimecode.sort((a, b) => {
      const timeA = parseTimecodeToSeconds(a.timecode);
      const timeB = parseTimecodeToSeconds(b.timecode);
      console.log(`🔍 排序比較: ${a.timecode} (${timeA}s) vs ${b.timecode} (${timeB}s)`);
      return timeA - timeB;
    });
    
    // 合併排序後的記錄（有時間碼的在前，無時間碼的保持原順序在後）
    const finalSortedRecords = [...sortedRecordsWithTimecode, ...recordsWithoutTimecode];
    
    console.log('📊 排序後順序:', finalSortedRecords.map(record => ({
      keyNumber: record.keyNumber,
      timestamp: record.timestamp,
      timecode: record.timecode
    })));
    
    // 更新記錄順序
    setNoteRecords(finalSortedRecords);
    
    // 通知父組件重新排序
    const newOrder = finalSortedRecords.map(item => item.keyNumber);
    onReorder(newOrder);
    
    console.log('✅ 自動排序完成');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth={false}
      fullScreen={isMobile}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      PaperProps={{
        sx: {
          width: isMobile ? '90%' : '95%',
          maxWidth: isMobile ? '90vw' : '400px', // 直式時最大寬度400px，橫式時90%視窗
          minHeight: isMobile ? '70vh' : '70vh', // 直式時高度維持在70%
          maxHeight: isMobile ? '70vh' : '80vh', // 直式時最大高度70%
          display: 'flex',
          flexDirection: 'column',
          margin: '15px auto',
          ...(isMobile && {
            borderRadius: 0,
            margin: '15px auto',
            maxHeight: '70vh' // 直式時最大高度70%
          })
        }
      }}
    >
      <DialogTitle sx={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        pb: 2,
        px: 3, // 增加左右padding
        pt: 3  // 增加頂部padding
      }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
          Note管理
        </Typography>
        
        {/* 變更版本按鈕 - 只有已登入用戶且有效影片URL時顯示 */}
        {isUserLoggedIn && videoUrl && user?.email && onVersionManagement && (
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={onVersionManagement}
            sx={{
              minWidth: '120px',
              height: '40px',
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              borderColor: '#1976d2',
              color: '#1976d2',
              px: 2, // 增加左右padding
              py: 1, // 增加上下padding
              '&:hover': {
                backgroundColor: '#f3f9ff',
                borderColor: '#1565c0',
                color: '#1565c0'
              }
            }}
          >
            變更版本
          </Button>
        )}
      </DialogTitle>

      <DialogContent sx={{ 
        p: 2, 
        pt: 2,
        px: 2,
        overflow: 'auto', 
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {noteRecords.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '200px',
            color: 'text.secondary'
          }}>
            <Typography variant="body1">
              沒有找到 Note 記錄
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[]}
          >
            <Box sx={{ 
              p: isMobile ? 1 : 1.5,
              px: 0.5,
              flex: 1,
              width: '100%',
              overflow: 'visible'
            }}>
              <SortableContext
                items={noteRecords.filter(record => 
                  record.timecode && record.timecode.trim() !== ''
                ).map((record, index) => `record-${index}-${record.keyNumber}`)}
                strategy={verticalListSortingStrategy}
              >
                {/* 數字鍵行 - 顯示所有有時間碼的記錄 */}
                {noteRecords.filter(record => 
                  record.timecode && record.timecode.trim() !== ''
                ).map((record, index) => {
                  const keyNumber = record.keyNumber;
                  
                  return (
                    <Box
                      key={keyNumber}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '50px 1fr' : '70px 1fr',
                        gap: isMobile ? 0.5 : 1,
                        mb: isMobile ? 0.5 : 1,
                        alignItems: 'center'
                      }}
                    >
                      {/* 固定的數字鍵欄位 - 字體變大，縮減右邊距 */}
                      <Box
                        onClick={() => {
                          // 點擊數字方塊時觸發時間碼跳轉
                          if (record && onTimecodeClick && record.timestamp > 0) {
                            console.log(`🎯 點擊數字方塊 ${keyNumber}，跳轉到時間碼:`, record.timestamp);
                            onTimecodeClick(record.keyNumber, record.timestamp);
                            // 自動關閉視窗
                            setTimeout(() => {
                              window.dispatchEvent(new CustomEvent('closeNoteRecordsDialog'));
                            }, 200);
                          }
                        }}
                        sx={{
                          width: isMobile ? '50px' : '70px',
                          height: isMobile ? '50px' : '70px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isMobile ? '20px' : '28px',
                          fontWeight: 'bold',
                          backgroundColor: record?.isActive ? theme.palette.primary.main : '#f5f5f5',
                          color: record?.isActive ? 'white' : theme.palette.text.secondary,
                          borderRadius: isMobile ? '12px' : '16px',
                          border: `2px solid ${record?.isActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
                          opacity: record?.isActive ? 1 : 0.6,
                          transition: 'all 0.2s ease',
                          cursor: record && record.timestamp > 0 ? 'pointer' : 'default',
                          '&:hover': {
                            transform: record && record.timestamp > 0 ? 'scale(1.05)' : 'none',
                            boxShadow: record?.isActive ? '0 4px 12px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)'
                          }
                        }}
                      >
                        {keyNumber}
                      </Box>

                      {/* 可拖拽的內容區域 */}
                      <Box
                        sx={{
                          minHeight: '50px',
                          border: `2px dashed ${record ? 'transparent' : theme.palette.grey[300]}`,
                          borderRadius: 1,
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: record ? '#e3f2fd' : theme.palette.grey[50]
                        }}
                      >
                        {record ? (
                          <SortableRecord
                            record={record}
                            index={index}
                            keyNumber={record.keyNumber}
                            isMobile={isMobile}
                            theme={theme}
                            onStartEdit={handleStartEdit}
                            onDelete={handleDelete}
                            onTimecodeClick={onTimecodeClick}
                          />
                        ) : (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ fontStyle: 'italic' }}
                          >
                            拖拽內容到此處
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </SortableContext>
            </Box>
          </DndContext>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 2, px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={handleOpenHelp}
            variant="outlined"
            color="info"
            title="查看使用說明"
          >
            說明
          </Button>
          <Button
            size="small"
            onClick={handleAutoSort}
            variant="outlined"
            color="secondary"
            startIcon={<SortIcon />}
            title="按時間碼從小到大排序"
          >
            按時間排序
          </Button>
        </Box>
        <Button onClick={onClose} color="primary">
          關閉
        </Button>
      </DialogActions>



      {/* 編輯 Note 對話框 */}
      <Dialog
        open={editingKey !== null}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: isMobile ? '75vh' : '70vh', // 手機版進一步減少高度
            maxHeight: isMobile ? '75vh' : '70vh',
            display: 'flex',
            flexDirection: 'column',
            // 移除Paper的預設padding
            '&.MuiPaper-root': {
              padding: 0
            },
            // 手機版特殊處理
            ...(isMobile && {
              height: '75vh',
              maxHeight: '75vh',
              width: '95vw',
              maxWidth: '95vw'
            })
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          // 手機版特殊處理
          ...(isMobile && {
            pb: 0.5, // 減少底部padding
            pt: 1,   // 減少頂部padding
          })
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mb: isMobile ? '4px' : '8px', // 手機版減少標題底部間距
          }}>
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                fontWeight: 'bold', 
                fontSize: isMobile ? '1.2rem' : '2rem', // 手機版減少字體大小
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                padding: isMobile ? '6px 12px' : '12px 24px', // 手機版減少padding
                borderRadius: '8px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              NO {editingKey}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          p: 0,
          position: 'relative',
          // 移除DialogContent的預設padding
          '&.MuiDialogContent-root': {
            padding: 0
          }
        }}>
                      <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              height: '100%',
              pt: 0, // 移除上方padding，因為標題已有8px底部間距
              px: 2,
              pb: '15px' // 加大底部padding到15px
            }}>


            {/* 文字輸入框區域 - 包含Tab切換和操作按鈕 */}
            <Box sx={{ 
              position: 'relative',
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* 統一的內容區域容器 - 編輯和預覽使用相同位置和尺寸 */}
              <Box sx={{ 
                position: 'relative',
                flex: 1,
                minHeight: 0,
                height: '100%', // 使用100%高度，響應父容器
                maxHeight: '100%', // 限制最大高度
                // 手機版特殊處理
                ...(isMobile && {
                  minHeight: '150px', // 手機版最小高度設為150px
                  maxHeight: 'calc(100vh - 200px)', // 手機版考慮對話框邊界
                })
              }}>
                {/* 編輯模式 - 絕對定位，與預覽模式重疊 */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: '42px', // 為底部按鈕留出空間
                  display: editMode === 'edit' ? 'block' : 'none',
                  zIndex: editMode === 'edit' ? 1 : 0,
                  width: '100%',
                  height: '100%', // 確保高度100%
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}>
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder="輸入 Note 內容... (支援 Markdown 格式)"
                    style={{
                      width: '100%',
                      height: '100%',
                      padding: '16px',
                      border: 'none',
                      outline: 'none',
                      fontFamily: 'monospace',
                      fontSize: isMobile ? '16px' : '14px',
                      resize: 'none',
                      lineHeight: '1.5',
                      boxSizing: 'border-box',
                      backgroundColor: 'transparent',
                      margin: 0,
                      // 手機橫式時的特殊處理
                      ...(isMobile && {
                        fontSize: '16px',
                        padding: '12px'
                      })
                    }}
                  />
                </Box>

                {/* 預覽模式 - 絕對定位，與編輯模式重疊 */}
                <Box sx={{
                  position: 'relative', // 為內部絕對定位提供參考
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: '42px', // 為底部按鈕留出空間
                  display: editMode === 'edit' ? 'none' : 'block',
                  zIndex: editMode === 'preview' ? 1 : 0,
                  width: '100%',
                  height: '100%', // 確保高度100%
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  overflow: 'auto',
                  boxSizing: 'border-box',
                  // 手機橫式時的特殊處理
                  ...(isMobile && {
                    padding: '12px'
                  })
                }}
                onMouseEnter={() => {
                  // 滑鼠進入時顯示編輯區域
                  const editArea = document.getElementById('md-edit-area');
                  if (editArea) {
                    editArea.style.opacity = '1';
                  }
                }}
                onMouseLeave={() => {
                  // 滑鼠離開時隱藏編輯區域
                  const editArea = document.getElementById('md-edit-area');
                  if (editArea) {
                    editArea.style.opacity = '0';
                  }
                }}
                >
                  {editText.trim() ? (
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: marked(editText, { breaks: true })
                      }}
                      sx={{
                        height: '100%',
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          margin: '16px 0 8px 0',
                          color: '#333'
                        },
                        '& p': {
                          margin: '8px 0',
                          lineHeight: '1.6'
                        },
                        '& code': {
                          backgroundColor: '#f0f0f0',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          fontFamily: 'monospace'
                        },
                        '& pre': {
                          backgroundColor: '#f5f5f5',
                          padding: '12px',
                          borderRadius: '4px',
                          overflow: 'auto',
                          border: '1px solid #e0e0e0'
                        },
                        '& blockquote': {
                          borderLeft: '4px solid #ddd',
                          margin: '16px 0',
                          padding: '8px 16px',
                          backgroundColor: '#f9f9f9'
                        },
                        '& ul, & ol': {
                          paddingLeft: '20px'
                        },
                        '& li': {
                          margin: '4px 0'
                        },
                        '& a': {
                          color: '#1976d2',
                          textDecoration: 'none'
                        },
                        '& a:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      沒有內容可預覽
                    </Typography>
                  )}
                  
                  {/* MD模式下的編輯區域 */}
                  <Box 
                    id="md-edit-area"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      width: '100%',
                      height: '100%', // 確保高度100%
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      zIndex: 10, // 確保在最上層
                      cursor: 'text', // 顯示文字編輯游標
                      opacity: 0, // 預設透明
                      transition: 'opacity 0.2s ease', // 平滑過渡
                      pointerEvents: 'auto' // 確保可以接收滑鼠事件
                    }}
                  >
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="在MD模式下直接編輯..."
                      style={{
                        width: '100%',
                        height: '100%',
                        padding: '16px',
                        border: 'none',
                        outline: 'none',
                        fontFamily: 'monospace',
                        fontSize: isMobile ? '16px' : '14px',
                        resize: 'none',
                        lineHeight: '1.5',
                        boxSizing: 'border-box',
                        backgroundColor: 'transparent',
                        margin: 0,
                        cursor: 'text',
                        // 手機橫式時的特殊處理
                        ...(isMobile && {
                          fontSize: '16px',
                          padding: '12px'
                        })
                      }}
                    />
                  </Box>
                </Box>
              </Box>

                             {/* 底部Tab控制欄 - 緊貼文字輸入框左下角 */}
                                                               <Box sx={{ 
                  position: 'relative', // 改為相對定位
                  bottom: 0, // 正常底部定位
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: 'white',
                  borderTop: '1px solid #e0e0e0',
                  px: 2,
                  py: 1,
                  mt: '5px', // 上方margin-top設為5px
                  // 手機直式時的特殊處理
                  ...(isMobile && {
                    px: 1,
                    py: 0.5,
                    // 手機直式時按鈕更小
                    '& .MuiButton-root': {
                      fontSize: '0.75rem',
                      minHeight: '32px',
                      padding: '4px 8px'
                    }
                  })
                }}>
                {/* 左邊：編輯/預覽Tab切換 */}
                <Box sx={{ 
                  display: 'flex',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0'
                }}>
                  <Button
                    onClick={() => setEditMode('edit')}
                    variant={editMode === 'edit' ? 'contained' : 'text'}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      borderRadius: editMode === 'edit' ? '6px' : '0',
                      backgroundColor: editMode === 'edit' ? theme.palette.primary.main : 'transparent',
                      color: editMode === 'edit' ? 'white' : theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: editMode === 'edit' ? theme.palette.primary.dark : '#e0e0e0'
                      },
                      // 確保icon和文字橫式排列
                      '& .MuiButton-startIcon': {
                        marginRight: '4px',
                        marginLeft: 0
                      }
                    }}
                    startIcon={<MarkdownIcon sx={{ fontSize: '16px' }} />}
                  >
                    編輯
                  </Button>
                  <Button
                    onClick={() => setEditMode('preview')}
                    variant={editMode === 'preview' ? 'contained' : 'text'}
                    size="small"
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      borderRadius: editMode === 'preview' ? '6px' : '0',
                      backgroundColor: editMode === 'preview' ? theme.palette.primary.main : 'transparent',
                      color: editMode === 'preview' ? 'white' : theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: editMode === 'preview' ? theme.palette.primary.dark : '#e0e0e0'
                      },
                      // 確保icon和文字橫式排列
                      '& .MuiButton-startIcon': {
                        marginRight: '4px',
                        marginLeft: 0
                      }
                    }}
                    startIcon={<PreviewIcon sx={{ fontSize: '16px' }} />}
                  >
                    MD格式預覽
                  </Button>
                </Box>

                {/* 右邊：操作按鈕 */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    onClick={handleCancelEdit} 
                    color="inherit"
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5
                    }}
                  >
                    取消
                  </Button>
                  <Button 
                    onClick={handleSaveEdit} 
                    color="primary" 
                    variant="contained"
                    size="small"
                    sx={{ 
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5
                    }}
                  >
                    保存
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default NoteRecordsDialog;
